<template>
<div v-show="store.state.currentTab === 'File'" class="b3-menu-content">
    <div id="my-projects-title-area">
        <h1 class="themed-title">
            {{t('my projects title', 'My Projects')}}
        </h1>
    </div>
    <div class="sort-and-filter">
        <span class="sort-area">
            <span>{{t('sort by','Sort by')}}&nbsp;&nbsp;</span>
            <select v-model="sortStyle">
                <option value="lowerTitle">{{t('name sort option','name')}}</option>
                <option value="dateModified">{{t('date modified sort option','date modified')}}</option>
                <option value="dateCreated">{{t('date created sort option','date created')}}</option>
            </select>
            <select v-model="sortDirection">
                <option value="_ASC">
                    {{ sortStyle === 'lowerTitle' ? 'A-Z' : t('recent first', 'Recent first') }}
                </option>
                <option value="_DESC">
                    {{ sortStyle === 'lowerTitle' ? 'Z-A' : t('oldest first', 'Oldest first')  }}
                </option>
            </select>
        </span>
        <span class="filter-area">
            <span>{{t('show', 'Show')}}&nbsp;&nbsp;</span>
            <select
                v-model="patternFilter"
                :style="patternFilter === 'All patterns' ? '' : 'background-color: gold;'"
            >
                <option
                    v-for="(element, i) in availablePatterns"
                    :value="element"
                >
                    {{ element }}
                </option>
            </select>
            <select
                v-model="libraryFilter"
                :style="libraryFilter === 'All libraries' ? '' : 'background-color: gold;'"
            >
                <option
                    v-for="(element, i) in availableLibraries"
                    :value="element"
                >
                    {{ element }}
                </option>
            </select>
        </span>
    </div>
    <div class="tag-area" v-if="allTags.length">
        <div v-if="tagFilter">
            <div class="tag-instructions">
                {{t('showing all projects with tag','Showing all projects with tag')}}:&nbsp;
                <span
                    class="tag-bubble"
                    :style="`background-color: ${getTagColor(tagFilter)};`"
                >
                    {{ tagFilter }}
                </span>
            </div>
            <button @click="tagFilter = ''">{{t('clear tag filter','Clear tag filter')}}</button>
        </div>
        <div v-else>
            <div class="tag-display-area">
                <span
                    v-for="(tag, j) in allTags" :key="j"
                    class="tag-bubble tag-bubble-clickable"
                    :style="`background-color: ${getTagColor(tag)};`"
                    @click="tagFilter = tag"
                >
                    {{ tag }}
                </span>
            </div>
        </div>
    </div>
    <div v-if="hasCheckedFiles">
        <button @click="deleteMultiple" class="warning-button">Delete Selected</button>
        <button @click="downloadMultiple">Download Selected</button>
    </div>
    <div class="pagination-area" v-else-if="pageArr.length > 1 && !loading">
        <button @click="previous" :disabled="onFirstPage">&lt;</button>
        <button
            @click="goToPage(num)"
            v-for="(num, i) in pageArr" :key="i"
            :class="currentResultsPage === num - 1 ? 'highlighted' : ''"
        >
            {{ num }}
        </button>
        <button @click="next" :disabled="onLastPage">&gt;</button>
    </div>
    <div class="table-holder">
        <table class="project-table" v-show="!loading">
            <tr
                v-for="(element, index) in store.state.browserResults" :key="index"
                class="project-row"
            >
                <td class="checkbox-col">
                    <input type="checkbox" v-model="element.checked">
                </td>
                <td
                    class="img-col"
                    @click="launchModal(element)"
                >
                    <img :src="element.thumbnail" alt="project image">
                </td>
                <td
                    @click="launchModal(element)"
                >
                    <table>
                        <tr>
                            <td>
                                <span class="project-title" :title="element.title">{{ myTruncate(element.title, 40) }}</span>&nbsp;
                                <span
                                    v-for="(tag, j) in element.tags" :key="j"
                                    class="tag-bubble"
                                    :style="`background-color: ${getTagColor(tag)};`"
                                >
                                    {{ tag }}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="project-description" :title="element.description">
                                    {{ myTruncate(element.description, 70) || t('no description', '(No description)')}}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td class="project-details">
                                {{ element.libraryDisplayName }} | {{ element.patternDisplayName }} | {{ niceDate(element.dateModified) }}
                            </td>
                        </tr>
                    </table>
                </td>
            </tr>
        </table>
        <!-- loading spinner -->
        <div v-show="loading" class="lds-default">
            <div v-for="(e, i) in new Array(12)" :key="i"></div>
        </div>
    </div>
    <div v-if="hasCheckedFiles">
        <button @click="deleteMultiple" class="warning-button">Delete Selected</button>
        <button @click="downloadMultiple">Download Selected</button>
    </div>
    <div class="pagination-area" v-else-if="pageArr.length > 1 && !loading">
        <button @click="previous" :disabled="onFirstPage">&lt;</button>
        <button
            @click="goToPage(num)"
            v-for="(num, i) in pageArr" :key="i"
            :class="currentResultsPage === num - 1 ? 'highlighted' : ''"
        >
            {{ num }}
        </button>
        <button @click="next" :disabled="onLastPage">&gt;</button>
    </div>
</div>
</template>

<script>
import { libraries } from '../../myapp/src/libraries';
import { patterns } from '../../myapp/src/patterns/patterns';
import { project } from '../../myapp/src/projects/project';
import { utils } from '../../myapp/src/utils';
import { beadCloud } from '../../myapp/src/core/azure';
import { save } from '../../myapp/src/projects/save';
import Icon from '../../components/Icon.vue';

export default {
    data() {
        return {
            hasOpenedMyProjects: false,
            currentResultsPage: 0,
            resultsPerPage: 10,
            pagesToShow: 9, // must be odd number
            sortStyle: 'dateModified',
            sortDirection: '_ASC',
            patternFilter: 'All patterns',
            libraryFilter: 'All libraries',
            tagFilter: '',
            loading: true,
            availablePatterns: ['All patterns'].concat(patterns.arr.map(e => e.displayName)),
            availableLibraries: ['All libraries'].concat(libraries.map(e => e.displayName)),
        }
    },
    watch: {
        sortDirection: function () {
            this.makeQuery({newQuery: true});
        },
        sortStyle: function () {
            this.makeQuery({newQuery: true});
        },
        patternFilter: function () {
            this.makeQuery({newQuery: true});
        },
        libraryFilter: function () {
            this.makeQuery({newQuery: true});
        },
        tagFilter: function () {
            this.makeQuery({newQuery: true});
        }
    },
    components: { Icon },
    computed: {
        allTags: function () {
            if (!loggedIn) return [];
            if (!this.store.state.tags?.sort) return [];
            return this.store.state.tags.sort((a, b) => a.toLowerCase() > b.toLowerCase() ? 1 : -1 );
        },
        pageArr: function () {
            const size = Math.min(this.numOfPages, this.pagesToShow);
            const startingIndex = this.currentResultsPage < (this.pagesToShow + 1) / 2 ?
                1 :
                this.currentResultsPage < this.numOfPages - ((this.pagesToShow - 1) / 2) ?
                    this.currentResultsPage - ((this.pagesToShow - 1) / 2) + 1 :
                    this.numOfPages - (this.pagesToShow - 1);

            return new Array(size).fill('').map((e, i) => i + startingIndex);
        },
        numOfPages: function () {
            return Math.ceil(this.store.state.browserResultCount / this.resultsPerPage);
        },
        onFirstPage: function () {
            return this.currentResultsPage === 0;
        },
        onLastPage: function () {
            return this.currentResultsPage >= this.numOfPages - 1;
        },
        hasCheckedFiles: function () {
            return this.store.state.browserResults.some(element => element.checked);
        }
    },
    methods: {
        niceDate: utils.niceDate,
        previous() {
            if (this.onFirstPage) return;
            this.currentResultsPage--;
            this.makeQuery();
        },
        next() {
            if (this.onLastPage) return;
            this.currentResultsPage++;
            this.makeQuery();
        },
        goToPage(num) {
            if (this.currentResultsPage === num - 1) return;
            this.currentResultsPage = num - 1;
            this.makeQuery();
        },
        makeQuery({newQuery=false, ignoreFilters=false} = {}) {

            if (newQuery) this.currentResultsPage = 0;

            this.loading = true;

            // swap sort direction for dates to stop oldest first being default
            const sortDirection = this.sortStyle === 'lowerTitle' ?
                this.sortDirection.slice(1) :
                this.sortDirection === '_ASC' ? 'DESC' : 'ASC';

            project.doBrowserQuery({
                sortDirection,
                orderBy: this.sortStyle,
                offset: this.currentResultsPage * this.resultsPerPage,
                limit: this.resultsPerPage,
                libraryFilter: ignoreFilters ? 'All libraries' : this.libraryFilter,
                patternFilter: ignoreFilters ? 'All patterns' : this.patternFilter,
                tagFilter: ignoreFilters ? '' : this.tagFilter,
            });
        },
        myTruncate: utils.myTruncate,
        launchModal(result) {
            this.store.mySet('selectedFile', result);
            this.store.mySet('showingModal', 'browser');
        },
        getTagColor: utils.getTagColor,
        async getInitialResults() {
            const { count } = await beadCloud({fn: 'getProjectCount', params: {currentUserId}});
            b3.store.mySet('projectCount', count || 0);
            save.updateTagList();
            this.makeQuery();
        },
        reset() {
            this.sortStyle = 'dateModified';
            this.sortDirection = '_ASC';
            this.patternFilter = 'All patterns';
            this.libraryFilter = 'All libraries';
            this.tagFilter = '';
            this.makeQuery();
        },
        upload() {
            project.upload();
        },
        deleteMultiple() {
            const doDelete = () => {
                this.store.state.browserResults
                    .filter(element => element.checked)
                    .forEach(file => {
                        project.deleteFile(file.id);
                    })
                b3.hideModal();
            };
            const prompt = this.t('delete multiple projects prompt', 'Delete selected projects?');

            b3.renderModal(prompt, doDelete, null);

        },
        downloadMultiple() {
            const files = this.store.state.browserResults.filter(element => element.checked);
            save.downloadMultiple(files);
        }
    },
    mounted() {
        this.ee.on('resultsLoaded', () => {
            this.loading = false;
        });
        this.ee.on('myProjectsActivated', () => {
            if (this.hasOpenedMyProjects) {
                // if we just cleared the cache, make a new query
                if (!Object.values(this.store.state.browserQueryCache).length) {
                    this.makeQuery();
                }
                return;
            };
            this.getInitialResults();
            this.hasOpenedMyProjects = true;
        });
        this.ee.on('launchMenu', () => {
            this.reset();
        });
    }
}
</script>

<style lang="scss">
@import '../../assets/colors.scss';

.sort-and-filter {
    select {
        margin-right: 10px;
        font-size: 16px;
    }
    .tiny-button {
        padding: 0;
        font-size: 5px;
        position: relative;
        top: 5px;
        left: 4px;
    }
    .sort-area, .filter-area {
        background-color: white;
        padding: 10px;
        border-radius: 5px;
        display: inline-block;
        margin: 10px 10px 0 0px;
    }
}
.tag-area {
    background-color: white;
    padding: 15px 20px 20px;
    border-radius: 5px;
    margin: 15px 0;
    .tag-instructions {
        padding: 10px 0;
    }
    .tag-title {
        display: inline-block;
        margin-right: 12px;
    }
}
#my-projects-title-area {
    margin: 10px 0px 5px;
    h1 {
        margin-bottom: 0px;
        margin-top: 0px;
    }
}

.table-holder {
    margin-top: 15px;
}
.tag-bubble {
    color: white;
    font-size: 14px;
    border-radius: 5px;
    margin-right: 5px;
    display: inline-block;
    padding: 3px 6px;
    margin-top: 4px;
}

.tag-bubble-clickable {
    &:hover {
        background-color: $highlight !important;
        cursor: pointer;
    }
}

.pagination-area {
    margin-top: 15px;
    button {
        background-color: rgba($color: #000000, $alpha: 0);
        border: $medium_grey 1px solid;
        color: black;
        padding: 6px 8px;
    }
    .highlighted {
        background-color: $highlight;
        color: white;
        &:hover {
            background-color: $highlight;
            cursor: default;
        }
    }
}
.project-table {
    width: 100%;
    border-collapse: collapse;
    .project-row {
        height: 110px;
        transition: 0.3s;
        &:hover {
            background-color: white;
            cursor: pointer;
        }
    }
    .project-title {
        font-size: 20px;
        font-weight: bold;
    }
    .project-description {
        color: $darker_grey;
    }
    .project-details {
        font-size: 14px;
    }
    .checkbox-col {
        width: 50px;
        input {
            width: 50px;
        }
    }
    .img-col {
        width: 120px;
        img {
            padding-top: 7px;
        }
    }
}
.icon-button {
    padding: 3px 4px 0;
}

</style>